import React, { useEffect, useState, useCallback } from "react";
import { Container } from "reactstrap";
import SimpleBar from "simplebar-react";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  FileManagerDirectoryDto,
  FileManagerMediaDto,
  PaginateQuery,
} from "../../interfaces";
import {
  useGetDirectoriesQuery,
  useGetFilesFromDirectoriesMutation,
} from "../../store/api/fileManagerApi";
import { MediaTypeEnum } from "../../enums";
import { FolderItem } from "./FolderItem";

const FileManagerPage = () => {
  //#region hooks
  const { t } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery(100);
  const [sidebarData, setSidebarData] = useState<any>();
  const [selectedPath, setSelectedPath] = useState<string>("");
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [fileList, setFileList] = useState<FileManagerMediaDto[]>([]);
  //#endregion

  document.title = `${t("documentTitle", { pageName: "fileManager" })}`;

  const { data: folders } = useGetDirectoriesQuery();
  const [getFilesFromDirectory, { isLoading: loadingFiles, data: files }] =
    useGetFilesFromDirectoriesMutation();

  function sidebarOpen(value: any) {
    const element = document.getElementsByTagName("body")[0];
    element.classList.add(value);
  }

  function sidebarClose(value: any) {
    const element = document.getElementsByTagName("body")[0];
    element.classList.remove(value);
  }

  useEffect(() => {
    sidebarOpen("file-detail-show");
  }, []);

  const handleLeafFolderClick = useCallback(
    (fullPath: string) => {
      setSelectedPath(fullPath);

      const query = { ...paginateQuery, page: 1 };

      setPaginateQuery(query);

      getFilesFromDirectory({
        data: query,
        directory: fullPath,
      });
    },
    [paginateQuery],
  );

  const changePage = (page: number) => {
    if (!selectedPath) return;

    const query = {
      ...paginateQuery,
      page,
    };

    setPaginateQuery(query);

    getFilesFromDirectory({
      data: query,
      directory: selectedPath,
    });
  };
  useEffect(() => {
    if (files?.data) {
      setFileList(files?.data);
    } else {
      setFileList([]);
    }
  }, [files]);

  const getFileIcon = (type: MediaTypeEnum) => {
    switch (type) {
      case MediaTypeEnum.IMAGE:
        return { icon: "ri-image-2-line", color: "info" };

      case MediaTypeEnum.VIDEO:
        return { icon: "ri-video-line", color: "danger" };

      case MediaTypeEnum.AUDIO:
        return { icon: "ri-music-2-line", color: "warning" };

      case MediaTypeEnum.DOCUMENT:
        return { icon: "ri-file-text-line", color: "secondary" };

      default:
        return { icon: "ri-file-line", color: "muted" };
    }
  };

  const getPaginationRange = (current: number, total: number, delta = 2) => {
    const start = Math.max(1, current - delta);
    const end = Math.min(total, current + delta);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const fileSidebar = () => {
    // @ts-ignore
    document.getElementById("file-overview").style.display = "block";
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="chat-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1">
            <div className="file-manager-sidebar">
              <div className="p-3 d-flex flex-column h-100">
                <div className="mb-3">
                  <h5 className="mb-0 fw-semibold">{t("fileManager")}</h5>
                </div>
                <div className="search-box">
                  <input
                    type="text"
                    className="form-control bg-light border-light"
                    placeholder="Search here..."
                  />
                  <i className="ri-search-2-line search-icon"></i>
                </div>
                <SimpleBar className="mt-3 mx-n4 px-4 file-menu-sidebar-scroll">
                  <ul className="list-unstyled file-manager-menu">
                    {folders?.map(
                      (folder: FileManagerDirectoryDto, index: number) => (
                        <FolderItem
                          key={`${folder.label}-${index}`}
                          item={folder}
                          parentPath=""
                          onLeafClick={handleLeafFolderClick}
                        />
                      ),
                    )}
                  </ul>
                </SimpleBar>
              </div>
            </div>
            <div
              className="file-manager-content w-100 p-3 py-0 d-flex flex-column"
              style={{ height: "100%" }}
            >
              <SimpleBar className="flex-grow-1 file-manager-content-scroll">
                <div id="folder-list" className="mb-2"></div>
                <div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle table-nowrap mb-0">
                      <thead className="table-active">
                        <tr>
                          <th>Name</th>
                          <th>File Item</th>
                          <th>File Size</th>
                          <th>Recent Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingFiles && (
                          <tr>
                            <td colSpan={5} className="text-center py-4">
                              Loading files...
                            </td>
                          </tr>
                        )}
                        {!loadingFiles && fileList.length === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center py-4 text-muted"
                            >
                              No files found
                            </td>
                          </tr>
                        )}
                        {!loadingFiles &&
                          fileList.map((item) => {
                            const { icon, color } = getFileIcon(item.type);
                            return (
                              <tr
                                key={item.id}
                                className="cursor-pointer"
                                onClick={() => {
                                  setSidebarData({
                                    fileName: item.name,
                                    size: `${(item.size / 1024).toFixed(2)} KB`,
                                    createDate: new Date(
                                      item.createdAt,
                                    ).toLocaleDateString(),
                                    fileType: item?.type,
                                    icon: getFileIcon(item.type).icon,
                                    iconClass: getFileIcon(item.type).color,
                                    path: item.path,
                                  });

                                  sidebarOpen("file-detail-show");
                                  fileSidebar();
                                }}
                              >
                                <td>
                                  <div className="d-flex align-items-center">
                                    <i
                                      className={`${icon} text-${color} me-2`}
                                    ></i>
                                    {item.name}
                                  </div>
                                </td>
                                <td>{item.type}</td>
                                <td>{(item.size / 1024).toFixed(2)} KB</td>
                                <td>
                                  {new Date(
                                    item.createdAt,
                                  ).toLocaleDateString()}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </SimpleBar>
              {/* Pagination at bottom */}
              {files?.meta && (
                <div className="mt-3 d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing{" "}
                    <span className="fw-semibold">
                      {(files.meta.currentPage - 1) * files.meta.itemsPerPage +
                        1}
                    </span>{" "}
                    –{" "}
                    <span className="fw-semibold">
                      {Math.min(
                        files.meta.currentPage * files.meta.itemsPerPage,
                        files.meta.totalItems,
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="fw-semibold">{files.meta.totalItems}</span>
                  </div>
                  <ul className="pagination mb-0">
                    <li
                      className={`page-item ${
                        files.meta.currentPage == 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => {
                          if (files?.meta)
                            changePage(files.meta?.currentPage - 1);
                        }}
                      >
                        ←
                      </button>
                    </li>
                    {getPaginationRange(
                      files.meta.currentPage,
                      files.meta.totalPages,
                    ).map((page) => (
                      <li
                        key={page}
                        className={`page-item ${
                          files.meta?.currentPage == page ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => changePage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        files.meta.currentPage == files.meta.totalPages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => {
                          if (files.meta) {
                            changePage(Number(files.meta.currentPage) + 1);
                          }
                        }}
                      >
                        →
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <div className="file-manager-detail-content p-3 py-0">
              <SimpleBar className="mx-n3 pt-3 px-3 file-detail-content-scroll">
                <div id="file-overview" className="h-100">
                  <div className="d-flex h-100 flex-column">
                    <div className="d-flex align-items-center pb-3 border-bottom border-bottom-dashed mb-3 gap-2">
                      <h5 className="flex-grow-1 fw-semibold mb-0">
                        File Preview
                      </h5>
                      <div>
                        <button
                          type="button"
                          className="btn btn-ghost-primary btn-icon btn-sm fs-16 favourite-btn"
                        >
                          <i className="ri-star-fill align-bottom"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-soft-danger btn-icon btn-sm fs-16 close-btn-overview"
                          onClick={() => sidebarClose("file-detail-show")}
                        >
                          <i className="ri-close-fill align-bottom"></i>
                        </button>
                      </div>
                    </div>

                    <div className="pb-3 border-bottom border-bottom-dashed mb-3">
                      <div className="file-details-box bg-light p-3 text-center rounded-3 border border-light mb-3">
                        <div className="display-4 file-icon">
                          <i
                            className={
                              sidebarData?.icon +
                              " text-" +
                              sidebarData?.iconClass
                            }
                          ></i>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-ghost-success float-end fs-16"
                      >
                        <i className="ri-share-forward-line"></i>
                      </button>
                      <h5 className="fs-16 mb-1 file-name">
                        {sidebarData?.fileName}
                      </h5>
                      <p className="text-muted mb-0 fs-12">
                        <span className="file-size">{sidebarData?.size}</span>,{" "}
                        <span className="create-date">
                          {sidebarData?.createDate}
                        </span>
                      </p>
                    </div>
                    <div>
                      <h5 className="fs-12 text-uppercase text-muted mb-3">
                        {t("fileDescription")} :
                      </h5>

                      <div className="table-responsive">
                        <table className="table table-borderless table-nowrap table-sm">
                          <tbody>
                            <tr>
                              <th scope="row" style={{ width: "35%" }}>
                                {t("fileName")} :
                              </th>
                              <td className="file-name">
                                {sidebarData?.fileName}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">{t("fileType")} :</th>
                              <td className="file-type">
                                {sidebarData?.fileType}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">Size :</th>
                              <td className="file-size">{sidebarData?.size}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("createdAt")} :</th>
                              <td className="create-date">
                                {sidebarData?.createDate}
                              </td>
                            </tr>
                            <tr>
                              <th scope="row">Path :</th>
                              <td className="file-path">
                                <div className="user-select-all text-truncate">
                                  {sidebarData?.path}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div>
                        <h5 className="fs-12 text-uppercase text-muted mb-3">
                          Share Information:
                        </h5>
                        <div className="table-responsive">
                          <table className="table table-borderless table-nowrap table-sm">
                            <tbody>
                              <tr>
                                <th scope="row" style={{ width: "35%" }}>
                                  Share Name :
                                </th>
                                <td className="share-name">\\*\Projects</td>
                              </tr>
                              <tr>
                                <th scope="row">Share Path :</th>
                                <td className="share-path">
                                  velzon:\Documents\
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto border-top border-top-dashed py-3">
                      <div className="hstack gap-2">
                        <button
                          type="button"
                          className="btn btn-soft-primary w-100"
                        >
                          <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                          {t("download")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </SimpleBar>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default FileManagerPage;
